import React, { useState, useEffect } from "react";
import * as yup from "yup";
import moment from "moment";
import { css, Global } from "@emotion/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { size, clone, map, get, last, filter } from "lodash";
import { useHistory, useParams, matchPath } from "react-router-dom";
import { Box, Grid, MenuItem, makeStyles, Typography } from "@material-ui/core";
import classNames from "classnames";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import styles from "assets/jss/material-dashboard-react/components/sidebarStyle.js";
import { TextField } from "./form/TextField";
import { GeneralFormErrors } from "./form/GeneralFormErrors";
import { PhotographUploader } from "./form/PhotographUploader";
import { GalleryPriceSelector } from "./form/GalleryPriceSelector";
import { setEditingGallery } from "../actions/gallery";
import { galleryList } from "../v2_actions/gallery";
import { handleSubmitResult } from "../v2_actions/form";
import { photographList } from "../v2_actions/photograph";
import { showSuccess } from "../v2_actions/error";
import { briefGalleryListForPhotoArt } from "v2_actions/brief_gallery";
import { photographerPriceList } from "../v2_actions/photographer_price";
import { loggedInUserLastName } from "../v2_actions/auth";
import { getUserStoredCountry } from "../v2_actions/auth_country";
import {
  activityTagDropdownOptions,
  albumDropdownOptions,
  galleryEventDropdownOptions,
  genreTagDropdownOptions,
} from "../v2_actions/dropdown_options";
import { StyledButton } from "../components/CustomButtons/StyledButton";
import { LoaderPercentProgressContainer } from "../views/uploadgallery/loaderComponent";
import { SessionNameFormField } from "./SessionNameFormField";
import { GalleryDateFormField } from "./GalleryDateFormField";
import { GalleryShotAtTimeFormField } from "./GalleryShotAtTimeField";
import { GalleryLocationFormField } from "./GalleryLocationFormField";
import { GalleryActivityFormField } from "./GalleryActivityFormField";
import { GalleryFormSectionHeading } from "./GalleryFormSectionHeading";
import { GalleryEventDetailsSection } from "./GalleryEventDetailsSection";
import { GalleryPasswordFormField } from "./GalleryPasswordFormField";
import { GalleryHideFormField } from "./GalleryHideFormField";
import { FailedPhotoUploadDialog } from "./FailedPhotoUploadDialog";
import { facialRecognitionList } from "v2_actions/facial_recognition";
import { processGalleryPhotographsList } from "v2_actions/process_gallery_photographs";

const useStyles = makeStyles(styles);
const DEFAULT_SHOT_AT_TIME_HOUR = 14;

const global_styles = css`
  #rightSideBar .MuiInput-underline::before,
  #rightSideBar .MuiInput-underline::after {
    display: none;
  }
  #passwordFieldWrapper p .MuiFormControl-root {
    width: 100%;
  }
`;

const MenuClass = {
  textField: {
    color: "black",
    fontWeight: "800",
    fontSize: "12px",
    textAlign: "left",
  },
  input: {
    color: "black",
    fontWeight: "800",
    fontSize: "12px",
    textAlign: "left",
  },
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 250,
    },
  },
  BlueBtn: {
    background: "#75A9F9",
  },
  input: {
    "&::placeholder": {
      textOverflow: "ellipsis !important",
      color: "blue",
    },
  },
  inputLabel: {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    width: "100%",
    color: "red",
  },
  circularProgressWheelWrapper: {
    position: "absolute",
    top: "50%",
    left: "50%",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "flex-start",
    height: "100%",
    transform: `translate(-50%, -50%)`,
    zIndex: 999,
  },
  circularProgressWheel: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "50%",
  },
  smallTextField: {
    margin: "0px 10px 0px 0px",
    background: "#ebebeb",
    height: 23,
    width: 45,
    // textDecoration: "underline",
    //
    "& input": {
      marginTop: 5,
      textAlign: "center",
      textDecoration: "none",
      padding: 0,
    },
  },
};

const disallowsNonAlphaNumericCharactersRegex = /^[a-zA-Z0-9 ]*$/;

const formSchema = yup.object().shape({
  session_name: yup
    .string()
    .matches(
      disallowsNonAlphaNumericCharactersRegex,
      "Special characters not allowed",
    ),
  lat: yup.number().required(),
  lng: yup.number().required(),
});

export const GalleryEditor = ({ match, field_name_prefix }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { gallery_id } = useParams();
  const country = getUserStoredCountry();
  const gallery = useSelector(() => galleryList.getObject(gallery_id));
  const galleryPhotographs = useSelector(() =>
    photographList.getGalleryPhotographs(),
  );
  const [tagsVal, setTagsVal] = useState(null);
  const [galleryType, setGalleryType] = useState("");
  const [genreTag, setGenreTag] = useState(null);
  const [openSports, setOpenSports] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sessionNameVal, setSessionNameVal] = useState("");
  const [descriptionVal, setDescriptionVal] = useState("");
  const [eventGallery, setEventGallery] = useState(null);
  const [album, setAlbum] = useState(null);
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [fileUploadsInProgress, setFileUploadsInProgress] = useState({});
  const numFilesSelectedForUpload = size(fileUploadsInProgress);
  const [galleryId, setGalleryId] = useState(null);
  const [failedPhotoUploads, setFailedPhotoUploads] = useState([]);
  const [openAddGalleryEventLogoDialog, setOpenAddGalleryEventLogoDialog] =
    useState(false);
  const [isFailedPhotoUploadDialogOpen, setIsFailedPhotoUploadDialogOpen] =
    useState(false);

  const genreTagOptions = useSelector(() =>
    genreTagDropdownOptions.getAsOptions(),
  );
  const galleryEventOptions = useSelector(() =>
    galleryEventDropdownOptions.getAsOptions(),
  );
  const albumOptions = useSelector(() => albumDropdownOptions.getAsOptions());
  const photographerPrice = useSelector(() =>
    photographerPriceList.getLoggedInPhotographerPrice(),
  );

  const isCreateNewForGallery = matchPath(history.location.pathname, {
    exact: true,
    strict: false,
    path: "/photographer/gallery/new",
  });
  const isCreateNewForPhotoArt = matchPath(history.location.pathname, {
    exact: true,
    strict: false,
    path: "/photographer/photo_art_gallery/new",
  });

  const numFilesCompleted = size(
    filter(
      fileUploadsInProgress,
      (f) =>
        f.status === "success" ||
        f.status == "failed" ||
        f.status == "duplicate",
    ),
  );

  const activityTagOptions = useSelector(() =>
    activityTagDropdownOptions.getAsOptionsWithEmptyOption({
      empty_label: "activity",
    }),
  );

  let activity_tag_options = activityTagOptions.filter(
    (val) => val.value !== "any",
  );

  const isNewPath = last(window.location.pathname.split("/")) === "new";
  const isEditorForPhotoArt =
    window.location.pathname.split("/").at(-2) === "photo_art_gallery";
  const selectedGallery = useSelector(
    (state) => !isNewPath && state.gallery.selectedGallery,
  );

  const [activityTags, setActivityTags] = useState(
    activity_tag_options
      .filter((tag) => selectedGallery?.activity_tags?.includes(tag.value))
      .map((i) => i?.original_item) ?? [],
  );

  const defaultValues = {
    lat: get(selectedGallery, "lat", null),
    lng: get(selectedGallery, "lng", null),
    tags: get(selectedGallery, "tags", ""),
    gallery_type: get(gallery, "gallery_type", "photos"),
    description: get(selectedGallery, "description", ""),
    genre_tags: get(selectedGallery, "genre_tags", null),
    activityTags: get(selectedGallery, "activity_tags", []),
    gallery_event: get(selectedGallery, "gallery_event", ""),
    album: get(selectedGallery, "album", ""),
    activityTags: get(selectedGallery, "activity_tags", []),
    session_name: get(selectedGallery, "session_name", null),
    session_name: get(selectedGallery, "session_name", null),
    shot_at_date: selectedGallery?.shot_at_datetime ?? new Date(),
    gallery_password: get(selectedGallery, "gallery_password", null),
    gallery_password: get(selectedGallery, "gallery_password", null),
    time_duration_hours: get(selectedGallery, "time_duration_hours", 2),
    available_to_purchase: get(selectedGallery, "available_to_purchase", true),
    add_gallery_event_logo: get(
      selectedGallery,
      "add_gallery_event_logo",
      null,
    ),
    can_index_for_facial_recognition: get(
      selectedGallery,
      "can_index_for_facial_recognition",
      false,
    ),
    shot_at_time: get(
      selectedGallery,
      "shot_at_datetime",
      moment(new Date())
        .set({ hour: DEFAULT_SHOT_AT_TIME_HOUR, minute: 0 })
        .toDate(),
    ),
    gallery_price: {
      currency_code: get(country, "currency_code"),
      country_code: get(country, "country_code"),
      base_price: get(selectedGallery, ["gallery_price", "base_price"], 0),
      low_res_price: get(
        selectedGallery,
        ["gallery_price", "low_res_price"],
        null,
      ),
      discount1_percentage: get(
        selectedGallery,
        ["gallery_price", "discount1_percentage"],
        0,
      ),
      discount2_percentage: get(
        selectedGallery,
        ["gallery_price", "discount2_percentage"],
        0,
      ),
      discount3_percentage: get(
        selectedGallery,
        ["gallery_price", "discount3_percentage"],
        0,
      ),
      discount4_percentage: get(
        selectedGallery,
        ["gallery_price", "discount4_percentage"],
        0,
      ),
      event_commission_percentage: get(
        selectedGallery,
        ["gallery_price", "event_commission_percentage"],
        0,
      ),
    },
  };

  const formProps = useForm({
    defaultValues,
    resolver: yupResolver(formSchema),
  });
  const {
    reset,
    control,
    setValue,
    getValues,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = formProps;

  const handleActivityTagChange = (event) => {
    setActivityTags(event.target.value);
  };

  const handleGenreTagChange = (genreId) => {
    setGenreTag(genreId);
    setValue("genre_tags", genreId);
  };

  useEffect(() => {
    if (selectedGallery) {
      dispatch(photographList.fetchGalleryPhotographs(selectedGallery.id));
    }
  }, [selectedGallery]);

  useEffect(() => {
    if (gallery && gallery.album) {
      setValue("album", gallery.album, { shouldValidate: true });
    }
    if (gallery && gallery.gallery_type) {
      setGalleryType(gallery.gallery_type);
      setValue("gallery_type", gallery.gallery_type, { shouldValidate: true });
    }
  }, [gallery]);

  useEffect(() => {
    setTimeout(() => {
      setValue("album", get(selectedGallery, "album"));
      setValue("gallery_event", get(selectedGallery, "gallery_event"));
      setValue(
        "can_index_for_facial_recognition",
        get(selectedGallery, "can_index_for_facial_recognition"),
      );
    }, 1000);
  }, []);

  useEffect(() => {
    if (eventGallery) {
      setGalleryType("event");
      setValue("gallery_type", "event", { shouldValidate: true });
    } else {
      setValue("gallery_event", "");
    }
  }, [eventGallery]);

  useEffect(() => {
    if (activityTags && openSports === true) {
      setOpenSports(false);
    }
  }, [activityTags]);

  useEffect(() => {
    if (gallery.activity_tags) {
      if (activity_tag_options.length > 0) {
        let old_activity_tags = [];
        gallery.activity_tags.map((activity_tag) => {
          let obj = activity_tag_options.filter(
            (activity_tag_option) => activity_tag_option.value === activity_tag,
          );
          if (obj) {
            old_activity_tags.push(obj[0].original_item);
          } else {
            return;
          }
        });
        if (JSON.stringify(activityTags) != JSON.stringify(old_activity_tags)) {
          setActivityTags(old_activity_tags);
        }
      }
    }
  }, [gallery.activity_tags, activity_tag_options, gallery]);
  useEffect(() => {
    dispatch(photographerPriceList.ensurePhotographerPriceLoaded());
    dispatch(activityTagDropdownOptions.fetchListIfNeeded());
    dispatch(albumDropdownOptions.fetchListIfNeeded());
    dispatch(galleryEventDropdownOptions.fetchListIfNeeded());
    dispatch(genreTagDropdownOptions.fetchListIfNeeded());
  }, []);

  useEffect(() => {
    dispatch(galleryList.ensureObjectLoaded(gallery_id));
    dispatch(activityTagDropdownOptions.fetchListIfNeeded());
    dispatch(albumDropdownOptions.fetchListIfNeeded());
    dispatch(galleryEventDropdownOptions.fetchListIfNeeded());
    dispatch(genreTagDropdownOptions.fetchListIfNeeded());
  }, [gallery_id]);

  useEffect(() => {
    if (gallery.activity_tags) {
      if (activity_tag_options.length > 0) {
        let old_activity_tags = [];
        gallery.activity_tags.map((activity_tag) => {
          let obj = activity_tag_options.filter(
            (activity_tag_option) => activity_tag_option.value === activity_tag,
          );
          if (obj) {
            old_activity_tags.push(obj[0].original_item);
          } else {
            return;
          }
        });
        if (JSON.stringify(activityTags) != JSON.stringify(old_activity_tags)) {
          setActivityTags(old_activity_tags);
        }
      }
    }
  }, [gallery.activity_tags, activity_tag_options, gallery]);

  useEffect(() => {
    if (selectedGallery?.gallery_price) {
      return;
    }

    if (gallery_id) {
      return;
    }
    if (!get(photographerPrice, "id")) {
      return;
    }
    const values = Object.assign({}, defaultValues);
    values.gallery_price = clone(photographerPrice);
    reset(values);
  }, [get(photographerPrice, "id")]);

  useEffect(() => {
    if (!gallery.id) {
      return;
    }
    const values = Object.assign({}, defaultValues, gallery);
    if (!values.gallery_price) {
      values.gallery_price = clone(defaultValues.gallery_price);
    }
    values.shot_at_date = moment(values.shot_at_datetime).toDate();
    values.shot_at_time = moment(values.shot_at_datetime).toDate();
    reset(values);
  }, [gallery.id]);

  const onSave = async (values) => {
    let gallery_type = isEditorForPhotoArt
      ? "photo_art"
      : (eventGallery && "event") || "photos";
    setSavingInProgress(true);
    values = {
      ...values,
      activity_tags: activityTags.map((val) => val.id),
      gallery_type: gallery_type,
      sport_tag_deprecated: null,
    };
    clearErrors();

    const on_file_upload_progress = function (file, status, errorInfo) {
      fileUploadsInProgress[file.path] = { status, errorInfo };
      setFileUploadsInProgress({ ...fileUploadsInProgress });
    };

    console.log("fileUploadsInProgress", fileUploadsInProgress);

    const on_all_saved = function (gallery_id, { successes, failures }) {
      let path;
      setGalleryId(gallery_id);
      dispatch(
        processGalleryPhotographsList.processGalleryPhotographs(gallery_id),
      );
      dispatch(
        facialRecognitionList.indexGalleryForFacialRecognition(gallery_id),
      );
      if (size(failures) > 0) {
        setFailedPhotoUploads(failures);
        setIsFailedPhotoUploadDialogOpen(true);
      } else {
        showSuccess("Success", `${size(successes)} photos saved`);
        onCancel();
        history.push("/photographer/map/");
      }

      if (isEditorForPhotoArt) {
        onCancel();
        history.push("/photographer/photo_art_gallery");
      }
      setSavingInProgress(false);
      dispatch(galleryList.invalidateList());
      dispatch(photographList.invalidateList());

      dispatch(briefGalleryListForPhotoArt.invalidateList());
      dispatch(briefGalleryListForPhotoArt.fetchListIfNeeded());
    };

    const on_gallery_save_ok = function (json) {
      const gallery = json;
      const gallery_id = gallery.id;
      const photos = get(values, "files");
      const gallery_type_value = getValues("gallery_type");

      dispatch(galleryList.invalidateList());
      dispatch(photographList.invalidateList());
      dispatch(briefGalleryListForPhotoArt.invalidateList());
      if (size(photos) === 0) {
        onCancel();
        isEditorForPhotoArt
          ? history.push(`/photographer/photo_art_gallery/`)
          : history.push(`/photographer/gallery/${gallery_id}/view`);
        if (gallery_id) {
          !isEditorForPhotoArt
            ? showSuccess("Success", "Gallery updated with no new photos")
            : showSuccess("Success", "Photo updated");
        } else {
          !isEditorForPhotoArt
            ? showSuccess("Success", "Empty gallery created")
            : showSuccess("Success", "Empty photo created");
        }
      } else {
        galleryList
          .savePhotos({
            files: photos,
            gallery: gallery,
            gallery_id: gallery_id,
            on_file_upload_progress,
            country_code: country.country_code,
            gallery_photographs: !isNewPath && galleryPhotographs,
          })
          .then((res) => {
            handleSubmitResult({
              res,
              formProps,
              on_ok: (res) => {
                on_all_saved(gallery_id, res);
              },
            });
          });
      }
    };

    values.shot_at_datetime = moment(values.shot_at_date)
      .set({
        hour: moment(values.shot_at_time).get("hour"),
        minute: moment(values.shot_at_time).get("minute"),
        second: 0,
        millisecond: 0,
      })
      .format();

    values.gallery_name = createGalleryName(values);

    if (selectedGallery && selectedGallery.id) {
      values.id = gallery_id;
      return dispatch(
        galleryList.saveObject({
          ...selectedGallery,
          ...values,
          id: selectedGallery.id,
        }),
      ).then((res) =>
        handleSubmitResult({ res, formProps, on_ok: on_gallery_save_ok }),
      );
    } else {
      return dispatch(galleryList.saveNewObject(values)).then((res) =>
        handleSubmitResult({ res, formProps, on_ok: on_gallery_save_ok }),
      );
    }
  };

  const onCancel = () => {
    dispatch(galleryList.invalidateList());
    dispatch(galleryList.fetchListIfNeeded());
    dispatch(setEditingGallery(null));
  };

  const createGalleryName = (values) => {
    let gallery_name;
    const user_last_name = loggedInUserLastName();
    const date = moment(values.shot_at_datetime).format("Do MMM 'YY");
    const first_time = moment(values.shot_at_datetime).format("HH:mm");
    const tag_Name = activityTags.map((tag) => ` ${tag.name}`).toString();

    if (galleryType !== "photo_art") {
      gallery_name = `${date}. ${first_time}, ${values.session_name}`;
    } else {
      gallery_name = values.session_name;
    }
    return gallery_name;
  };

  const handleUnavailableToPurchaseGallery = (event) => {
    setValue("available_to_purchase", !event.target.checked);
  };

  const handleIndexGalleryForFacialRecognition = (event) => {
    const isIndexGalleryForFacialRecognitionChecked = event.target.checked;
    if (!getValues("can_index_for_facial_recognition")) {
      setValue(
        "can_index_for_facial_recognition",
        isIndexGalleryForFacialRecognitionChecked,
        { shouldValidate: true },
      );
    }
  };

  const handleCloseCustomGalleryEventLogoDialog = () => {
    setOpenAddGalleryEventLogoDialog(false);
  };

  const handleAddGalleryEventLogo = (event) => {
    const addLogoIsChecked = event.target.checked;
    setValue("add_gallery_event_logo", addLogoIsChecked, {
      shouldValidate: true,
    });
    if (addLogoIsChecked) {
      setOpenAddGalleryEventLogoDialog(true);
    }
  };
  const handleHideGallery = (event) => {
    const isAvailableToPurchase = event.target.checked;
    setValue("available_to_purchase", isAvailableToPurchase, {
      shouldValidate: true,
    });
  };

  const handleCloseFailedPhotoUploadDialog = () => {
    setIsFailedPhotoUploadDialogOpen(false);
    onCancel();
    history.push(`/photographer/gallery/${galleryId}/view`);
  };

  const renderTagsField = () => {
    return (
      <Grid item xs={12}>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Typography className={classes.subMenu} variant="subtitle1">
            Tags:
          </Typography>
          <Typography className={classes.subMenu9} variant="subtitle1">
            <Controller
              name="tags"
              control={control}
              className={classes.inputController}
              render={(fieldProps, onTagsValChanged) => (
                <TextField
                  type="text"
                  variant="standard"
                  placeholder="Enter Tags"
                  fieldProps={fieldProps}
                  className={classes.textField}
                  onTagsValChanged={setTagsVal(fieldProps.field.value)}
                />
              )}
            />
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderDescriptionField = () => {
    return (
      <Grid item xs={12}>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Typography variant="subtitle1" className={classes.subMenu}>
            Photo Description
          </Typography>
          <Typography variant="subtitle1" className={classes.subMenu9}>
            <Controller
              name="description"
              control={control}
              render={(fieldProps, onDescriptionValChanged) => (
                <TextField
                  type="text"
                  variant="contained"
                  disableUnderline
                  multiline
                  maxRows={4}
                  variant="standard"
                  fieldProps={fieldProps}
                  className={classes.textField}
                  onDescriptionValChanged={setDescriptionVal(
                    fieldProps.field.value,
                  )}
                  placeholder="Enter a short story about the picture"
                />
              )}
            />
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderAvailableToPurchaseField = () => {
    return (
      <Grid item xs={12}>
        <Box
          sx={{
            gap: 5,
            height: 20,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="subtitle1"
            className={classes.addRecognition}
            sx={{
              width: "90%",
            }}
          >
            Hide Gallery
          </Typography>
          <Typography className={classes.addRecognitionRadio}>
            <Checkbox
              size="small"
              icon={<RadioButtonUncheckedIcon />}
              checkedIcon={<RadioButtonCheckedIcon />}
              onChange={(event) => handleUnavailableToPurchaseGallery(event)}
            />
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderGalleryPriceSelectorField = () => {
    return (
      <Grid item xs={12}>
        <GalleryPriceSelector
          gallery={gallery}
          formProps={formProps}
          gallery_id={gallery_id}
          gallery_type={galleryType}
          field_name_prefix="gallery_price"
          isEditorForPhotoArt={isEditorForPhotoArt}
        />
      </Grid>
    );
  };

  const renderGalleryGenreField = () => {
    return (
      <Grid item xs={12}>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Typography variant="subtitle1" className={classes.subMenuExtended}>
            Genre
          </Typography>
          <Typography
            variant="subtitle1"
            className={classNames(classes.event, classes.secondSection)}
          >
            <Controller
              name="genre"
              control={control}
              render={(fieldProps, onEventValChanged) => (
                <TextField
                  select
                  fieldProps={fieldProps}
                  className={classes.textField}
                  placeholder="Enter if Applicable"
                  onGenreTagValChanged={handleGenreTagChange(
                    fieldProps.field.value,
                  )}
                >
                  <MenuItem value={null}>
                    <span className={classes.drop}>{"No Genre"}</span>
                  </MenuItem>
                  {map(genreTagOptions, (v, index) => (
                    <MenuItem value={v.value} key={index}>
                      <span className={classes.drop}>{v.label}</span>
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Typography>
        </Box>
      </Grid>
    );
  };

  const renderGalleryFields = () => {
    return (
      <>
        <GalleryFormSectionHeading heading="Gallery Details" />
        <Box my={2}>
          <SessionNameFormField
            control={control}
            classes={classes}
            setSessionNameVal={setSessionNameVal}
            isEditorForPhotoArt={isEditorForPhotoArt}
          />
        </Box>
        <Box my={2}>
          <GalleryDateFormField
            control={control}
            classes={classes}
            isEditorForPhotoArt={isEditorForPhotoArt}
          />
        </Box>
        <Box my={2}>
          <GalleryShotAtTimeFormField
            control={control}
            classes={classes}
            isEditorForPhotoArt={isEditorForPhotoArt}
          />
        </Box>
        <Box my={2}>
          <GalleryActivityFormField
            control={control}
            classes={classes}
            MenuClass={MenuClass}
            formProps={formProps}
            openSports={openSports}
            activityTags={activityTags}
            setOpenSports={setOpenSports}
            selectedGallery={selectedGallery}
            isEditorForPhotoArt={isEditorForPhotoArt}
            activity_tag_options={activity_tag_options}
            handleActivityTagChange={handleActivityTagChange}
          />
        </Box>
        <Box my={2}>
          <GalleryLocationFormField
            control={control}
            classes={classes}
            formProps={formProps}
            isEditorForPhotoArt={isEditorForPhotoArt}
          />
        </Box>
        <Box my={2}>
          <GalleryHideFormField
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            control={control}
            classes={classes}
            setValue={setValue}
            getValues={getValues}
            formProps={formProps}
            setEventGallery={setEventGallery}
            handleHideGallery={handleHideGallery}
            galleryEventOptions={galleryEventOptions}
            handleAddGalleryEventLogo={handleAddGalleryEventLogo}
            openAddGalleryEventLogoDialog={openAddGalleryEventLogoDialog}
            handleCloseCustomGalleryEventLogoDialog={
              handleCloseCustomGalleryEventLogoDialog
            }
          />
        </Box>
        <GalleryEventDetailsSection
          control={control}
          classes={classes}
          setValue={setValue}
          getValues={getValues}
          formProps={formProps}
          setEventGallery={setEventGallery}
          setAlbum={setAlbum}
          galleryEventOptions={galleryEventOptions}
          albumOptions={albumOptions}
          handleAddGalleryEventLogo={handleAddGalleryEventLogo}
          openAddGalleryEventLogoDialog={openAddGalleryEventLogoDialog}
          handleIndexGalleryForFacialRecognition={
            handleIndexGalleryForFacialRecognition
          }
          handleCloseCustomGalleryEventLogoDialog={
            handleCloseCustomGalleryEventLogoDialog
          }
        />
        <Box my={2}>
          <GalleryPasswordFormField
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            control={control}
            classes={classes}
            setValue={setValue}
            getValues={getValues}
            formProps={formProps}
            setEventGallery={setEventGallery}
            galleryEventOptions={galleryEventOptions}
            handleAddGalleryEventLogo={handleAddGalleryEventLogo}
            openAddGalleryEventLogoDialog={openAddGalleryEventLogoDialog}
            handleCloseCustomGalleryEventLogoDialog={
              handleCloseCustomGalleryEventLogoDialog
            }
          />
        </Box>
        {/* { renderAvailableToPurchaseField() } */}
        {renderGalleryPriceSelectorField()}
      </>
    );
  };

  const renderPhotoArtFields = () => {
    return (
      <>
        <SessionNameFormField
          control={control}
          classes={classes}
          setSessionNameVal={setSessionNameVal}
          isEditorForPhotoArt={isEditorForPhotoArt}
        />
        <GalleryDateFormField
          control={control}
          classes={classes}
          isEditorForPhotoArt={isEditorForPhotoArt}
        />
        <GalleryLocationFormField
          control={control}
          classes={classes}
          formProps={formProps}
          isEditorForPhotoArt={isEditorForPhotoArt}
        />
        {renderGalleryGenreField()}
        {renderTagsField()}
        {renderDescriptionField()}
        {renderGalleryPriceSelectorField()}
      </>
    );
  };

  const renderForm = () => {
    return (
      <form onSubmit={handleSubmit(onSave)}>
        <GeneralFormErrors formProps={formProps} />
        <Grid container className={classes.rightSideBarWrapper}>
          <Grid item xs={12}>
            <div
              className={classNames(
                classes.sidebarHeading,
                classes.galleryEditorSideBarHeading,
              )}
            >
              {get(
                selectedGallery,
                "gallery_name",
                "Enter New Gallery Details",
              )}
            </div>
          </Grid>
          <Box className={classes.galleryFieldsWrapper}>
            {!isEditorForPhotoArt && renderGalleryFields()}
            {isEditorForPhotoArt && renderPhotoArtFields()}
          </Box>
          {savingInProgress && (
            <Grid item xs={12} style={{ textAlign: "center" }}>
              <div className={classes.circularProgressWrapper}>
                <div className={classes.circularProgressWhell}>
                  <CircularProgress
                    size={50}
                    mode="indeterminate"
                    style={{ color: "green" }}
                  />
                </div>
              </div>
            </Grid>
          )}
          <Box className={classes.galleryFieldsWrapper}>
            <GalleryFormSectionHeading heading="Upload Photos" />
          </Box>
          <Grid item xs={12} className={classes.photographUploaderWrapper}>
            <PhotographUploader
              savingInProgress={savingInProgress}
              numFilesCompleted={numFilesCompleted}
              fileAnnotations={fileUploadsInProgress}
              maxFileLimit={isEditorForPhotoArt ? 1 : null}
              isEditorForPhotoArt={isEditorForPhotoArt}
              numFilesSelectedForUpload={numFilesSelectedForUpload}
              uploadMessage={
                isEditorForPhotoArt ? "Add Photo" : "Add photos to gallery"
              }
              onChange={({ files }) =>
                setValue("files", files, { shouldValidate: true })
              }
            />
          </Grid>
          <Grid item xs={6} className={classes.buttonWrapper}>
            <StyledButton
              className={classes.grayButton}
              onClick={() => {
                onCancel();
                isEditorForPhotoArt
                  ? history.push("/photographer/photo_art_gallery")
                  : (window.location.href = "/photographer/map");
              }}
            >
              Cancel
            </StyledButton>
          </Grid>
          <Grid item xs={6} className={classes.buttonWrapper} type="submit">
            <div className={classes.submitAndCancelBtn}>
              <Grid item xs={6}>
                {isEditorForPhotoArt && (
                  <StyledButton
                    type="submit"
                    className={`${classes.buttonWrapper} ${
                      savingInProgress && classes.grayButton
                    }`}
                  >
                    Save Photo
                  </StyledButton>
                )}
                {!isEditorForPhotoArt && (
                  <StyledButton
                    type="submit"
                    className={`${classes.buttonWrapper} ${
                      savingInProgress && classes.grayButton
                    }`}
                  >
                    {get(selectedGallery, "id")
                      ? "Update Gallery"
                      : "Create Gallery"}
                  </StyledButton>
                )}
              </Grid>
            </div>
          </Grid>
        </Grid>
      </form>
    );
  };

  return (
    <div className={classes.galleryEditorPage}>
      <Global styles={global_styles} />
      {!gallery_id && (
        <Box display="flex" alignItems="center" paddingLeft="32px">
          <Typography
            className={classes.titletypography}
            style={{ textAlign: "center" }}
          >
            <font className={classes.loadGalleryTitle}>
              {/* Load up a New Gallery */}
            </font>
          </Typography>
        </Box>
      )}
      {savingInProgress && (
        <div className={classes.saving}>
          <div className={classes.progressWrapper}>
            <LoaderPercentProgressContainer />
          </div>
        </div>
      )}
      {renderForm()}
      <FailedPhotoUploadDialog
        open={isFailedPhotoUploadDialogOpen}
        failedPhotoUploads={failedPhotoUploads}
        handleClose={handleCloseFailedPhotoUploadDialog}
      />
    </div>
  );
};
